import { Component, Vue, Watch } from 'vue-property-decorator'
import { inject } from 'inversify-props'
import API from '@core/services/API'
import AppDatatable from '@core/components/AppDatatable.vue'
import AppForm from '@core/components/AppForm.vue'
import { FormStructure } from '@master/model/structure/FormStructure'
import { IDateService } from '@master/services/IDateService'
import { UserCPDMetaList } from '@core/model/common/UserCPDMetaList'
import { header, UserCPDMeta } from '@core/model/common/UserCPDMeta'
import { groupBy, isUndefined } from 'lodash/fp'
import { CpdGlobals } from './model/CpdGlobals'
import { UIService } from '@core/services/UIService'
import { SharedVariable } from '@core/model/SharedVariable'
import { UserMetaList } from '@core/model/common/UserMetaList'
import { downloadCSVFile } from '@core/services/CSVService'
import AppDialog from '@core/components/AppDialog.vue'
import { RequiredHours } from '@core/model/common/RequiredHours'

@Component({
  components: {
    AppDatatable,
    AppForm,
    AppDialog
  }
})
export class CpdSummaryMixin extends Vue {
  @inject()
  protected dateService: IDateService

  @inject()
  protected $api: API

  @inject()
  private sharedVariable: SharedVariable

  uiService: UIService

  protected cpdMetaList: UserCPDMetaList = null
  protected newCpd: UserCPDMeta = new UserCPDMeta()
  protected newCpdAttendees = []
  protected selectAttendeesMode = false
  protected txtSearchUser = ''

  protected activeTab: 'byCourse' | 'byEntry' | 'requiredHours' = 'byCourse'
  protected txtSearch = ''
  protected showModal = false
  protected showRequiredHoursDialog = false
  protected errorMessage = ''
  protected userMetaList: UserMetaList = null
  protected userMetaHeader = ['_oid', 'names', 'fullname', 'adviser_branch', 'status', 'systemrole', 'company_code', 'mas_repno', 'email', 'mobile', 'adviser_products', 'other_adviser_products']

  protected globals: CpdGlobals = new CpdGlobals()
  protected showUsersListModal = false

  formattedDate (unixTime: number | string) {
    return this.dateService.format(Number(unixTime), 'YYYY-MM-DD')
  }

  get byCourselist () {
    const courses = groupBy('courseId', this.byEntrylist)
    return Object.keys(courses).map(courseId => {
      const course = courses[courseId][0]
      course.faCoreEthicsHours = course.faCoreEthicsHours ?? 0
      course.faCoreRegHours = course.faCoreRegHours ?? 0
      course.faSuppMsHours = course.faSuppMsHours ?? 0
      course.faSuppOthersHours = course.faSuppOthersHours ?? 0
      course.giOthers = course.giOthers ?? 0

      return {
        ...course || {},
        participants: courses[courseId].length || 0
      }
    })
  }

  get byEntrylist () {
    return !this.cpdMetaList ? [] : this.cpdMetaList.list.map(cpd => {
      cpd.faCoreEthicsHours = cpd.faCoreEthicsHours ?? 0
      cpd.faCoreRegHours = cpd.faCoreRegHours ?? 0
      cpd.faSuppMsHours = cpd.faSuppMsHours ?? 0
      cpd.faSuppOthersHours = cpd.faSuppOthersHours ?? 0
      cpd.giOthers = cpd.giOthers ?? 0

      return {
        ...cpd,
        startDateMillisDisplay: this.dateService.format(cpd.startDateMillis, 'YYYY-MM-DD'),
        endDateMillisDisplay: this.dateService.format(cpd.endDateMillis, 'YYYY-MM-DD'),
        emit: true,
        present: this.getPresentDisplayString(cpd.present)
      }
    })
  }

  getPresentDisplayString (value: number) {
    if (value == null) return ''
    if (value === 1) return 'Yes'
    if (value === 0) return 'No'
    return ''
  }

  get byCourseColumns () {
    return [
      { label: 'Course Title', field: 'courseTitle' },
      { label: 'Course Provider', field: 'courseProvider' },
      { label: 'Course Venue', field: 'courseVenue' },
      { label: 'From', field: 'startDateMillisDisplay' },
      { label: 'To', field: 'endDateMillisDisplay' },
      { label: 'FA Core Ethics Hours', field: 'faCoreEthicsHours' },
      { label: 'FA Core Reg Hours', field: 'faCoreRegHours' },
      { label: 'FA Supp MS Hours', field: 'faSuppMsHours' },
      { label: 'FA Supp Others Hours', field: 'faSuppOthersHours' },
      { label: 'GI Hours', field: 'giOthers' }
    ]
  }

  get byEntryColumns () {
    return [
      { label: 'Username', field: 'username' },
      { label: 'Full Name', field: 'userFullname' },
      { label: 'Course Title', field: 'courseTitle' },
      { label: 'From', field: 'startDateMillisDisplay' },
      { label: 'To', field: 'endDateMillisDisplay' },
      { label: 'FA Core Ethics Hours', field: 'faCoreEthicsHours' },
      { label: 'FA Core Reg Hours', field: 'faCoreRegHours' },
      { label: 'FA Supp MS Hours', field: 'faSuppMsHours' },
      { label: 'FA Supp Others Hours', field: 'faSuppOthersHours' },
      { label: 'GI Hours', field: 'giOthers' },
      { label: 'Present', field: 'present' },
      { label: 'Assigned To (Year)', field: 'assignedTo' }
    ]
  }

  get requireHoursColumns () {
    return [
      { label: 'Year', field: 'globalCpdReqYear' },
      { label: 'FA Core Ethics Hours', field: 'globalCpdReqEthics' },
      { label: 'FA Core Reg Hours', field: 'globalCpdReqReg' },
      { label: 'FA Supp MS Hours', field: 'globalCpdReqSuppMs' },
      { label: 'FA Supp Others Hours', field: 'globalCpdReqSuppOthers' },
      { label: 'GI Hours', field: 'globalCpdReqGi' }
    ]
  }

  get newCpdStructure (): FormStructure<UserCPDMeta> {
    return [
      {
        label: 'Course Title*',
        key: 'courseTitle',
        disabled: this.selectAttendeesMode
      },
      {
        label: 'Course Provider*',
        key: 'courseProvider',
        disabled: this.selectAttendeesMode
      },
      {
        label: 'Course Venue*',
        key: 'courseVenue',
        disabled: this.selectAttendeesMode
      },
      {
        label: 'FA Core Ethics Hours*',
        key: 'faCoreEthicsHours',
        type: 'number',
        show: !this.selectAttendeesMode
      },
      {
        label: 'FA Core Reg Hours*',
        key: 'faCoreRegHours',
        type: 'number',
        show: !this.selectAttendeesMode
      },
      {
        label: 'FA Supp MS Hours*',
        key: 'faSuppMsHours',
        type: 'number',
        show: !this.selectAttendeesMode
      },
      {
        label: 'FA Supp Others Hours*',
        key: 'faSuppOthersHours',
        type: 'number',
        show: !this.selectAttendeesMode
      },
      {
        label: 'GI Hours*',
        key: 'giOthers',
        type: 'number',
        show: !this.selectAttendeesMode
      },
      {
        label: 'Period From*',
        key: 'startDateMillis',
        type: 'date',
        show: !this.selectAttendeesMode,
        onChange: (oldVal, newVal) => {
          try {
            this.newCpd.assignedTo = Number(new Date(newVal).getFullYear())
            this.newCpd.endDateMillis = newVal
          } catch (ex) { console.error(ex) }
        }
      },
      {
        label: 'Period To*',
        key: 'endDateMillis',
        type: 'date',
        show: !this.selectAttendeesMode
      },
      {
        label: 'Duration*',
        key: 'duration',
        show: !this.selectAttendeesMode
      },
      {
        label: 'Assigned To (Year)*',
        key: 'assignedTo',
        type: 'number',
        show: !this.selectAttendeesMode
      },
      {
        label: 'Description*',
        key: 'courseDescription',
        type: 'textarea',
        show: !this.selectAttendeesMode,
        classes: ['colspan']
      }
    ]
  }

  get requiredHoursStructure (): FormStructure<CpdGlobals> {
    return [
      {
        label: 'Year',
        key: 'globalCpdReqYear'
      },
      {
        label: 'FA Core Ethics Hours',
        key: 'globalCpdReqEthics',
        type: 'number'
      },
      {
        label: 'FA Core Reg Hours',
        key: 'globalCpdReqReg',
        type: 'number'
      },
      {
        label: 'FA Supp MS Hours',
        key: 'globalCpdReqSuppMs',
        type: 'number'
      },
      {
        label: 'FA Supp Others Hours',
        key: 'globalCpdReqSuppOthers',
        type: 'number'
      },
      {
        label: 'GI Hours',
        key: 'globalCpdReqGi',
        type: 'number'
      }
    ]
  }

  get userList () {
    return !this.userMetaList ? [] : this.userMetaList.getFullList()
  }

  get userListColumns () {
    return [
      { label: 'Username', field: 'names' },
      { label: 'Full Name', field: 'fullname' },
      { label: 'System Role', field: 'systemrole' }
    ]
  }

  handleAttendeesSelection (rows: any[]) {
    this.newCpdAttendees = rows
  }

  get selectedAttendees () {
    return this.newCpdAttendees.map(attd => {
      /* eslint-disable @typescript-eslint/camelcase */
      return {
        user_oid: attd.oid,
        user_fullname: attd.fullname,
        username: attd.names[0]
      }
      /* eslint-enable */
    })
  }

  @Watch('selectAttendeesMode')
  async toggleAttendees (showAttendeesList) {
    if (showAttendeesList && this.userMetaList === null) await this.fetchUsers()
  }

  addNewCpd () {
    this.showModal = true
    this.errorMessage = ''
    this.newCpd = new UserCPDMeta()
    this.newCpdAttendees = []
    this.selectAttendeesMode = false
  }

  clickCourse (row: UserCPDMeta) {
    window.location.href = `/live/cpd/course/${row.courseId}`
  }

  clickCourseEntry (row: UserCPDMeta) {
    this.$router.push({ name: 'User CPD Records', params: { userOid: row.userOid } })
  }

  async toggleAttendeesMode () {
    if (!this.selectAttendeesMode && !this.validateNewCpd()) {
      this.errorMessage = 'Please complete all fields'
      return
    }
    this.selectAttendeesMode = !this.selectAttendeesMode
    this.errorMessage = ''
  }

  checkUnsavedCpd (done) {
    const emptyCpd = new UserCPDMeta()
    const hasUnsavedData = Object.keys(this.newCpd).some(cpdKey => this.newCpd[cpdKey] !== emptyCpd[cpdKey])
    if (hasUnsavedData && !confirm('All unsaved data will be removed. Confirm?')) return
    done()
  }

  validateNewCpd () {
    let valid = true
    Object.keys(this.newCpd)
      .filter((key) => !['oid', 'userOid', 'userFullname', 'username', 'present', 'courseId'].includes(key))
      .forEach((key) => {
        if ((this.newCpd[key] === '' || !this.newCpd[key]) && typeof this.newCpd[key] !== 'number') valid = false
      })
    return valid
  }

  async saveNewCpd () {
    const valid = this.validateNewCpd()
    this.errorMessage = ''
    if (!valid) {
      this.errorMessage = 'Please complete all fields'
      return
    }
    if (this.selectedAttendees.length === 0) {
      this.errorMessage = 'Please select at least one attendee'
      return
    }

    /* eslint-disable @typescript-eslint/camelcase */
    await this.$api.cpd.course.new({
      course_info: this.newCpd.convertToRawData(),
      attendees: this.selectedAttendees
    })
    /* eslint-enable */
    this.showModal = false
    await this.fetchData()
  }

  async fetchData () {
    const res = await this.$api.cpd.list({
      headers: JSON.stringify(header)
    })
    this.cpdMetaList = new UserCPDMetaList(res.data)
  }

  async fetchUsers () {
    const resUser = await this.$api.account.list({
      groupStatus: 'user',
      headers: JSON.stringify(this.userMetaHeader),
      query: 'status = ?',
      queryArgs: 'active'
    })
    this.userMetaList = new UserMetaList(resUser.data, this.userMetaHeader)
  }

  async fetchGlobalValues () {
    const response = await this.$api.assumptions.get({
      keys: this.globals.getAllRawKeys()
    })

    Object.assign(this.globals, this.globals.extractData(response.values))
  }

  async setGlobalValues () {
    const response = await this.$api.assumptions.update({
      keyValues: this.globals.convertToRawData()
    })
    if (!response.error) {
      await this.uiService.alert.show({
        type: 'success',
        message: 'Required Hours global values are saved successfully'
      })
      this.showRequiredHoursDialog = false
    }
  }

  getCurrentColsAndList () {
    let colsConfig = []
    let dataList = []
    let name = 'cpd-data'
    if (this.activeTab === 'byEntry') {
      colsConfig = this.byEntryColumns
      dataList = this.byEntrylist
      name = 'cpd-entry'
    } else if (this.activeTab === 'byCourse') {
      colsConfig = this.byCourseColumns
      dataList = this.byCourselist
      name = 'cpd-course'
    }
    const labels = colsConfig.map(col => col.label)
    const entries = dataList.map(course => {
      const formatted = {}
      colsConfig.forEach(config => {
        formatted[config.label] = course[config.field]
      })
      return formatted
    })

    return { cols: labels, list: entries, name }
  }

  exportCSV () {
    const { cols, list, name } = this.getCurrentColsAndList()
    console.log(cols, list, name)
    downloadCSVFile(cols, list, name)
  }

  async showPopulateDefaultHoursModal () {
    if (!this.userMetaList) {
      await this.fetchUsers()
    }

    this.newCpdAttendees = []
    this.showUsersListModal = true
  }

  async populateDefaultHours () {
    if (this.newCpdAttendees.length === 0) {
      return
    }

    /* eslint-disable @typescript-eslint/camelcase */
    const cpd = new RequiredHours()

    cpd.year = this.globals.globalCpdReqYear
    cpd.ethics = this.globals.globalCpdReqEthics
    cpd.reg = this.globals.globalCpdReqReg
    cpd.suppMs = this.globals.globalCpdReqSuppMs
    cpd.suppOthers = this.globals.globalCpdReqSuppOthers
    cpd.gi = this.globals.globalCpdReqGi

    if (!cpd.allIsFilled()) {
      await this.uiService.alert.show({
        type: 'warning',
        message: 'Global CPD Hours value is empty. Please fill in accordingly.'
      })
      return
    }

    const keys = ['year', 'ethics', 'reg', 'suppMs', 'suppOthers', 'gi']
    const data = {
      adviser_oid_list: JSON.stringify(this.newCpdAttendees.map(attd => attd.oid)),
      data: JSON.stringify(cpd.convertToCustomRawData(keys))
    }
    /* eslint-enable */

    await this.$api.cpd.saveMany(data)

    await this.uiService.alert.show({
      type: 'success',
      message: 'Global CPD Hours has successfully populated to the selected users'
    })

    this.showUsersListModal = false
  }

  async mounted () {
    this.uiService = this.sharedVariable.uiService
    await this.fetchData()
    await this.fetchGlobalValues()
  }
}
